/**
 * Single source of truth for the columns of every list page that the TMA
 * admin can show or hide per client ("Column Visibility" on the client page).
 *
 * Rules that keep the admin switches and the pages in step:
 *  - The object key (e.g. `devices`) is the storage key under
 *    `client.visibleColumns` and the key every page passes to `useTableColumns`.
 *  - `columns[].key` are the page's real column ids. Pages must provide a cell
 *    renderer for every key here and for nothing else; TypeScript enforces it
 *    through `ColumnKey<T>` in `useTableColumns`, so the two cannot drift.
 *  - `requiredColumn` can never be hidden: the row's identifier. "Hide All"
 *    in the admin keeps it, because an empty list means "unrestricted", not
 *    "nothing".
 *  - `defaultVisible`, when set, is what an unrestricted client sees; otherwise
 *    all columns.
 *  - `navLabel` is the menu label of the page, used as the admin tab title so
 *    both sides call the page the same thing.
 *
 * Actions and selection checkboxes are not columns here: always visible.
 */

export interface TableColumnConfig {
  readonly key: string;
  /** i18n key of the column header. */
  readonly label: string;
}

export interface TableConfig {
  /** i18n key of the menu entry for this page. */
  readonly navLabel: string;
  readonly columns: readonly TableColumnConfig[];
  /** Column that is always visible (the row identifier). */
  readonly requiredColumn: string;
  /** Columns an unrestricted client sees. Omitted = all. */
  readonly defaultVisible?: readonly string[];
}

export const tableColumnsConfig = {
  sites: {
    navLabel: "nav.sites",
    requiredColumn: "name",
    columns: [
      { key: "name", label: "sites.columns.name" },
      { key: "location_text", label: "sites.columns.location" },
      { key: "updated_at", label: "sites.columns.lastUpdated" },
    ],
  },
  devices: {
    navLabel: "nav.rfidScanners",
    requiredColumn: "name",
    columns: [
      { key: "is_active", label: "devices.columns.active" },
      { key: "name", label: "devices.columns.name" },
      { key: "mobility_type", label: "devices.columns.mobility" },
      { key: "hardware_type", label: "devices.columns.hardware" },
      { key: "status_processing_type", label: "devices.columns.statusProcessing" },
      { key: "site", label: "devices.columns.site" },
      { key: "last_seen", label: "devices.columns.lastSeen" },
    ],
  },
  vehicles: {
    navLabel: "nav.vehicles",
    requiredColumn: "name",
    columns: [
      { key: "id", label: "vehicles.columns.id" },
      { key: "name", label: "vehicles.columns.name" },
      { key: "rfid", label: "vehicles.columns.rfid" },
      { key: "vehicle_model", label: "vehicles.columns.model" },
      { key: "site", label: "vehicles.columns.site" },
      { key: "assigned_driver", label: "vehicles.columns.driver" },
      { key: "maintenance", label: "vehicles.columns.maintenance" },
      { key: "updatedAt", label: "vehicles.columns.lastUpdated" },
    ],
  },
  vehicleModels: {
    navLabel: "nav.vehicleModels",
    requiredColumn: "name",
    columns: [
      { key: "id", label: "vehicleModels.columns.id" },
      { key: "name", label: "vehicleModels.columns.name" },
      { key: "brand", label: "vehicleModels.columns.brand" },
      { key: "model", label: "vehicleModels.columns.model" },
      { key: "size", label: "vehicleModels.columns.size" },
      { key: "updatedAt", label: "vehicleModels.columns.lastUpdated" },
    ],
  },
  items: {
    navLabel: "nav.items",
    requiredColumn: "external_id",
    columns: [
      { key: "createdAt", label: "items.columns.dateOfCreation" },
      { key: "external_id", label: "items.columns.externalId" },
      { key: "last_scan", label: "items.columns.lastScan" },
      { key: "last_moved", label: "items.columns.lastMoved" },
      { key: "rfid_tags", label: "items.columns.rfid" },
      { key: "status", label: "items.columns.status" },
      { key: "category", label: "items.columns.category" },
      { key: "location", label: "items.columns.location" },
      { key: "related_count", label: "items.columns.linkedItems" },
      { key: "times_scanned", label: "items.columns.timesScanned" },
      { key: "hawb", label: "items.columns.hawb" },
    ],
    // "times_scanned" is opt-in: the backend only counts it for clients whose
    // saved visibility includes it, so it must stay out of the default set.
    defaultVisible: [
      "createdAt",
      "external_id",
      "last_scan",
      "last_moved",
      "rfid_tags",
      "status",
      "category",
      "location",
      "related_count",
      "hawb",
    ],
  },
  products: {
    navLabel: "nav.categories",
    requiredColumn: "name",
    columns: [
      { key: "name", label: "products.columns.name" },
      { key: "sku", label: "products.form.sku" },
      { key: "variant", label: "products.columns.variant" },
      { key: "cost", label: "products.columns.cost" },
      { key: "retail_price", label: "products.columns.retailPrice" },
      { key: "procurement_time", label: "products.columns.procurementTime" },
      { key: "filling_capacity", label: "products.columns.fillingCapacity" },
      { key: "tare_weight", label: "products.columns.tareWeight" },
      { key: "number_of_items", label: "products.columns.numberOfItems" },
      { key: "number_of_sites", label: "products.columns.numberOfSites" },
      { key: "last_moved", label: "products.columns.lastMoved" },
    ],
    defaultVisible: [
      "name",
      "sku",
      "variant",
      "cost",
      "number_of_items",
      "number_of_sites",
      "last_moved",
    ],
  },
  templates: {
    navLabel: "nav.templates",
    requiredColumn: "name",
    columns: [
      { key: "name", label: "templates.columns.name" },
      { key: "description", label: "templates.columns.description" },
      { key: "product", label: "templates.columns.category" },
      { key: "site", label: "templates.columns.site" },
      { key: "location", label: "templates.columns.location" },
      { key: "createdAt", label: "templates.columns.createdAt" },
    ],
  },
  rfidTags: {
    navLabel: "nav.rfidTags",
    requiredColumn: "rfid",
    columns: [
      { key: "rfid", label: "rfidTags.columns.rfid" },
      { key: "type", label: "rfidTags.columns.type" },
      { key: "linked_to", label: "rfidTags.columns.linkedTo" },
      { key: "last_scanned_by", label: "rfidTags.columns.scanner" },
      { key: "last_scan", label: "rfidTags.columns.lastScan" },
      { key: "createdAt", label: "rfidTags.columns.createdAt" },
      { key: "updatedAt", label: "rfidTags.columns.lastUpdated" },
    ],
  },
  shipmentLogs: {
    navLabel: "nav.shipmentLogs",
    requiredColumn: "label",
    columns: [
      { key: "id", label: "shipments.columns.id" },
      { key: "label", label: "shipments.columns.label" },
      { key: "vehicle", label: "shipments.columns.vehicle" },
      { key: "items", label: "shipments.columns.items" },
      { key: "location", label: "shipments.columns.location" },
      { key: "scanner", label: "shipments.columns.scanner" },
      { key: "date", label: "shipments.columns.date" },
      { key: "updatedAt", label: "shipments.columns.lastUpdated" },
    ],
  },
  shipmentManifests: {
    navLabel: "nav.shipmentManifests",
    requiredColumn: "awb_number",
    columns: [
      { key: "awb_number", label: "manifests.columns.awb" },
      { key: "hawb_number", label: "manifests.columns.hawb" },
      { key: "arrival_date", label: "manifests.columns.arrival" },
      { key: "origin_station", label: "manifests.columns.origin" },
      { key: "packages", label: "manifests.columns.packages" },
      { key: "weight_kg", label: "manifests.columns.weight" },
      { key: "storage_fee_paid", label: "manifests.columns.feePaid" },
      { key: "blocked", label: "manifests.columns.blocked" },
      { key: "has_pick_list", label: "manifests.columns.pickList" },
      { key: "release", label: "manifests.columns.release" },
      { key: "tagged", label: "manifests.columns.tagged" },
      { key: "exit_status", label: "manifests.columns.exitStatus" },
    ],
  },
} as const satisfies Record<string, TableConfig>;

export type TableKey = keyof typeof tableColumnsConfig;
/** The column ids of one table, as a union of string literals. */
export type ColumnKey<T extends TableKey> = (typeof tableColumnsConfig)[T]["columns"][number]["key"];

export const getTableNames = (): TableKey[] => Object.keys(tableColumnsConfig) as TableKey[];

export const getTableConfig = (tableKey: string): TableConfig | undefined =>
  (tableColumnsConfig as Record<string, TableConfig>)[tableKey];

export const getTableColumnKeys = (tableKey: string): string[] =>
  getTableConfig(tableKey)?.columns.map((column) => column.key) ?? [];

/** Columns an unrestricted client sees: the declared default set, or every column. */
export const getDefaultVisibleColumnKeys = (tableKey: string): string[] => {
  const config = getTableConfig(tableKey);
  return [...(config?.defaultVisible ?? config?.columns.map((column) => column.key) ?? [])];
};
